import { useEffect, useRef, useCallback } from 'react';
import { useDebounce } from './useDebounce';
import { devLog } from '@/utils/logger';

/**
 * useAutosave Hook
 *
 * Auto-save data to localStorage with debouncing
 *
 * Supports team-scoping: if teamId is provided, the key becomes `team_${teamId}_${key}`
 * to prevent data leakage across teams (e.g., agency staff in God Mode).
 *
 * @param data - Data to save
 * @param options - Configuration options
 * @returns Methods to restore and clear saved data
 */

interface AutosaveOptions<T> {
  key: string; // localStorage key
  delay?: number; // Debounce delay in ms (default 30000 = 30s)
  onSave?: (data: unknown) => void; // Optional callback on save
  enabled?: boolean; // Enable/disable autosave (default true)
  teamId?: number | null; // Optional team ID for scoping (if provided, key becomes team_{teamId}_{key})
  /**
   * Predicate used to decide whether `data` is "effectively empty" (e.g. no
   * caption, no media, no channels). When provided, autosave (both the
   * debounced write and `flush()`) will SKIP writing to localStorage while
   * the predicate returns true, so an empty/reset state can never clobber a
   * genuine draft that's already saved.
   */
  isEmpty?: (data: T) => boolean;
}

/**
 * Wire format written to localStorage: the raw data plus a timestamp so
 * callers can show "saved X ago" without a second storage read.
 */
interface StoredEntry<T> {
  data: T;
  savedAt: number;
}

function isStoredEntry<T>(value: unknown): value is StoredEntry<T> {
  return typeof value === 'object' && value !== null && 'data' in value && 'savedAt' in value;
}

/**
 * Parse a raw localStorage string into a StoredEntry. Tolerates the legacy
 * (pre-savedAt-wrapper) format, where the raw data was stored directly with
 * no envelope, by wrapping it with `savedAt: Date.now()` on read.
 */
function parseStoredEntry<T>(raw: string): StoredEntry<T> | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isStoredEntry<T>(parsed)) {
      return parsed;
    }
    // Legacy format: raw T with no wrapper.
    return { data: parsed as T, savedAt: Date.now() };
  } catch {
    return null;
  }
}

export function useAutosave<T>(data: T, options: AutosaveOptions<T>) {
  const { key, delay = 30000, onSave, enabled = true, teamId, isEmpty } = options;
  const debouncedData = useDebounce(data, delay);
  const isFirstRender = useRef(true);
  const lastSavedData = useRef<string | null>(null);
  // Keep a live ref to the current (non-debounced) data so flush() can read it synchronously.
  const currentDataRef = useRef<T>(data);
  currentDataRef.current = data;
  const suppressedSerialized = useRef<string | null>(null);

  // Compute effective key (team-scoped if teamId provided)
  const effectiveKey = teamId ? `team_${teamId}_${key}` : key;

  const prevDebouncedRef = useRef(debouncedData);

  useEffect(() => {
    // The debounced value moving is the ONLY trigger. A change of `enabled` / key / team must never write:
    // the debounced value lags the live data by up to `delay`, so e.g. leaving edit mode (enabled false ->
    // true) would otherwise write the STALE pre-edit content into this (new-post) slot.
    const debouncedChanged = prevDebouncedRef.current !== debouncedData;
    prevDebouncedRef.current = debouncedData;

    // Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!debouncedChanged) return;

    // Skip if autosave is disabled, or the caller scopes by team but has no
    // team right now (writing would hit the UNSCOPED key and could bleed into
    // another team's composer).
    if (!enabled || teamId === null) {
      return;
    }

    // Never let an effectively-empty draft overwrite a real saved draft
    // (e.g. right after a form reset).
    // Always judge and write the LIVE data (not the lagging debounced copy): after a save + clear/reset the
    // live form is already empty or latched, while the debounced copy can still hold the saved content.
    const liveData = currentDataRef.current;
    if (isEmpty?.(liveData)) {
      suppressedSerialized.current = null; // empty form: latch has done its job; don't keep suppressing
      return;
    }

    // Serialize data (comparison excludes the savedAt wrapper on purpose,
    // so unrelated re-renders with identical content don't re-write).
    const serializedData = JSON.stringify(liveData);

    if (suppressedSerialized.current !== null) {
      if (serializedData === suppressedSerialized.current) return; // still the cleared content — don't resurrect
      suppressedSerialized.current = null;                         // genuinely new content — release latch
    }

    // Skip if data hasn't changed
    if (serializedData === lastSavedData.current) {
      return;
    }

    // Save to localStorage
    try {
      const entry: StoredEntry<T> = { data: liveData, savedAt: Date.now() };
      localStorage.setItem(effectiveKey, JSON.stringify(entry));
      lastSavedData.current = serializedData;

      // Optional callback
      onSave?.(liveData);

      devLog.log(
        `[Autosave] Draft saved at ${new Date().toLocaleTimeString()}${teamId ? ` (team ${teamId})` : ''}`
      );
    } catch (error) {
      devLog.error('[Autosave] Failed to save to localStorage:', error);
    }
  }, [debouncedData, effectiveKey, onSave, enabled, teamId, isEmpty]);

  /**
   * Restore data from localStorage
   */
  const restore = useCallback((): T | null => {
    try {
      const saved = localStorage.getItem(effectiveKey);
      if (saved) {
        const entry = parseStoredEntry<T>(saved);
        if (entry) {
          devLog.log(`[Autosave] Draft restored from localStorage${teamId ? ` (team ${teamId})` : ''}`);
          return entry.data;
        }
      }
    } catch (error) {
      devLog.error('[Autosave] Failed to restore from localStorage:', error);
    }
    return null;
  }, [effectiveKey, teamId]);

  /**
   * Get the timestamp (ms epoch) the current saved draft was written at, or
   * null if there's nothing saved / it can't be parsed. Used to show
   * "saved X ago" in a restore prompt without triggering a restore log.
   */
  const getSavedAt = useCallback((): number | null => {
    try {
      const saved = localStorage.getItem(effectiveKey);
      if (saved) {
        const entry = parseStoredEntry<T>(saved);
        return entry?.savedAt ?? null;
      }
    } catch (error) {
      devLog.error('[Autosave] Failed to read savedAt from localStorage:', error);
    }
    return null;
  }, [effectiveKey]);

  /**
   * Clear saved data from localStorage
   */
  const clear = useCallback(() => {
    try {
      localStorage.removeItem(effectiveKey);
      lastSavedData.current = null;
      // Latch so a pending debounce tick or a lifecycle flush() can't resurrect
      // the draft we just committed+cleared; releases automatically when content changes.
      suppressedSerialized.current = JSON.stringify(currentDataRef.current);
      devLog.log(`[Autosave] Draft cleared from localStorage${teamId ? ` (team ${teamId})` : ''}`);
    } catch (error) {
      devLog.error('[Autosave] Failed to clear localStorage:', error);
    }
  }, [effectiveKey, teamId]);

  /**
   * Check if there's saved data
   */
  const hasSaved = useCallback((): boolean => {
    try {
      const saved = localStorage.getItem(effectiveKey);
      return !!saved;
    } catch (error) {
      return false;
    }
  }, [effectiveKey]);

  /**
   * Flush: synchronously write the *current* (non-debounced) data to localStorage.
   * Call this on lifecycle events that precede a freeze/close/reload (tab hidden,
   * pagehide, beforeunload) so content typed within the debounce window (see the
   * `delay` option) is preserved before unmount. Respects the same `isEmpty`
   * guard as the debounced write — an empty draft is never flushed over a real one.
   */
  const flush = useCallback((): void => {
    if (!enabled || teamId === null) return;
    if (isEmpty?.(currentDataRef.current)) { suppressedSerialized.current = null; return; }
    try {
      const serialized = JSON.stringify(currentDataRef.current);
      if (suppressedSerialized.current !== null && serialized === suppressedSerialized.current) return;
      const entry: StoredEntry<T> = { data: currentDataRef.current, savedAt: Date.now() };
      localStorage.setItem(effectiveKey, JSON.stringify(entry));
      lastSavedData.current = serialized;
      suppressedSerialized.current = null;
      devLog.log(
        `[Autosave] Draft flushed synchronously${teamId ? ` (team ${teamId})` : ''}`
      );
    } catch (error) {
      devLog.error('[Autosave] Failed to flush to localStorage:', error);
    }
  }, [effectiveKey, enabled, teamId, isEmpty]);

  return { restore, clear, hasSaved, flush, getSavedAt };
}
